import React from 'react';
import * as ReactDOM from 'react-dom';
import { useRouterHistory } from 'react-router';
import '@jimdo/ui/dist/index.css';
import { createHistory } from 'history';
import Root from './containers/Root';
import configureStore from './redux/configureStore';
import bootstrap from './modules/bootstrap';
import {
  AppState,
  websiteIsReady,
  shouldPollLoginLink,
  getLang,
  getUsername,
} from './redux/reducers';
import getConfig from './config';
import { isUserMobile, getMobileOS } from './modules/mobileDetection';
import { clearPersistedState, getPersistedState } from './modules/persistence';
import { getLocation, getDocument } from './modules/helpers';
import { retrievePrices, mobileSettings } from './redux/actions/options';
import { clearTemporaryState, userHadPersistedState } from './redux/actions/global';
import { retrieveLoginLink } from './redux/actions/websiteCreation';
import { setUserIdToken } from './redux/actions/user';
import {
  ensureLanguageCookieIsset,
  getLanguageCookie,
  getUserIdTokenFromCookie,
} from './modules/cookies';
import { loadTrackingScript, initBunchbox, trackDWH, TrackingLabel } from './modules/tracking';
import { AppFactory } from './modules/bootstrap';
import { TRACKING_ASSETS_ENDPOINT } from './modules/constants';
import { Router } from './containers/Router';
import CoreLayout from './layouts/CoreLayout';
import { initRum } from '@jimdo/monitoring';
import { ExperimentsProvider, JimdoApp } from '@jimdo/growth-tools';
import { TrackingEvent } from '@jimdo/frontend-tracking';

const { language, jimdoEnvironment } = getConfig();

const destroy = (root: Element) => () => {
  ReactDOM.unmountComponentAtNode(root);
};

const shouldClearPersistedState = (state: AppState): boolean => {
  if (websiteIsReady(state)) {
    return true;
  }

  const loc = getLocation();
  if (!loc) {
    return false;
  }

  const lang = getLanguageCookie();

  if (state && state.options && lang !== state.options.lang) {
    return true;
  }

  return loc.pathname === `/${language}/`.replace(/\/\//g, '/');
};

export const createTheSignupFlow = (): AppFactory => (root: Element) => {
  initRum({
    applicationId: '068e2510-91db-480e-b91e-4011d3373163',
    clientToken: 'pubc4907473ad8d05d059caf6d73ea294d9',
    service: 'the-signup-flow',
    env: jimdoEnvironment,
    version: 'CONSTANT-FOR-NOW', // we need to send git hash by changing the webpack
    shouldRecordSession: false,
  });

  const history = useRouterHistory(createHistory)({
    basename: `/${language}`,
  });

  ensureLanguageCookieIsset();

  let storedState = getPersistedState();
  if (shouldClearPersistedState(storedState)) {
    clearPersistedState();
    storedState = null;
  }
  const initialState = !storedState ? undefined : storedState;
  const store = configureStore(initialState, history);

  const loc = getLocation();

  const doc = getDocument();

  if (storedState && loc && doc) {
    store.dispatch(userHadPersistedState(loc.pathname, doc.referrer));
  }

  // see https://github.com/Jimdo/the-signup-flow/issues/24
  // we need to make sure if we have a restored state that no temporary visual effects are still present
  store.dispatch(clearTemporaryState());

  const os = getMobileOS();
  const isMobile = isUserMobile();

  store.dispatch(setUserIdToken(getUserIdTokenFromCookie()));
  store.dispatch(mobileSettings(os, isMobile));
  store.dispatch(retrievePrices());

  if (shouldPollLoginLink(storedState)) {
    const storedUsername = getUsername(storedState);
    const storedLang = getLang(storedState);
    if (storedUsername && storedLang) {
      store.dispatch(retrieveLoginLink(storedUsername, storedLang));
    }
  }

  const trackEvent = (event: TrackingEvent) => {
    trackDWH('experiment', event.label as TrackingLabel, event.payload);
  };

  const destroyer = destroy(root);
  // Now that redux and react-router have been configured, we can render the
  // React application to the DOM!

  const { createdAt } = getConfig();
  ReactDOM.render(
    <Root store={store}>
      <ExperimentsProvider
        dateJoined={createdAt}
        language={language}
        jimdoApp={JimdoApp.Signup}
        track={trackEvent}
      >
        <CoreLayout>
          <Router store={store} history={history} />
        </CoreLayout>
      </ExperimentsProvider>
    </Root>,
    root
  );

  return destroyer;
};

bootstrap(language, createTheSignupFlow);
loadTrackingScript(TRACKING_ASSETS_ENDPOINT);
initBunchbox();
